import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Manages the data layer for a collection view: stale-while-revalidate fetching,
 * in-place item mutations, and the `onItemActions` callback bridge.
 *
 * @param {{
 *   fetchItems: () => Promise<Array>,
 *   getCachedItems?: () => Promise<{ items: Array }>,
 *   getItemId?: (item: Object) => string,
 *   onItemsLoaded?: (items: Array) => void,
 *   onItemActions?: ({ replaceItem, removeItem }) => void,  // replaceItem(id, fields) returns false if id isn't loaded
 *   refreshKey: string | number,
 * }} options
 */
export function useCollectionData({
    fetchItems,
    getCachedItems,
    getItemId,
    onItemsLoaded,
    onItemActions,
    refreshKey,
}) {
    const [items, setItems] = useState([]);
    // Mirror of `items` so replaceItem can report synchronously whether it found the item.
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [staleWarning, setStaleWarning] = useState(null);
    const hasLoadedRef = useRef(false);
    const lastFetchRef = useRef(0);

    // Stable refs so loadItems closure never goes stale
    const fetchItemsRef = useRef(fetchItems);
    fetchItemsRef.current = fetchItems;
    const getCachedItemsRef = useRef(getCachedItems);
    getCachedItemsRef.current = getCachedItems;
    const onItemsLoadedRef = useRef(onItemsLoaded);
    onItemsLoadedRef.current = onItemsLoaded;

    const loadItems = useCallback(async () => {
        setError(null);

        // Phase 1: serve cached data instantly on first load
        if (!hasLoadedRef.current && getCachedItemsRef.current) {
            try {
                const cached = await getCachedItemsRef.current();
                if (cached?.items?.length > 0) {
                    setItems(cached.items);
                    hasLoadedRef.current = true;
                    setLoading(false);
                    onItemsLoadedRef.current?.(cached.items);
                }
            } catch { /* cache miss — fall through to fresh fetch */ }
        }

        if (!hasLoadedRef.current) setLoading(true);

        // Phase 2: background refresh
        try {
            const result = await fetchItemsRef.current();
            setItems(result);
            hasLoadedRef.current = true;
            lastFetchRef.current = Date.now();
            setStaleWarning(null);
            onItemsLoadedRef.current?.(result);
        } catch (err) {
            if (hasLoadedRef.current) {
                setStaleWarning('Couldn’t refresh — showing cached data.');
            } else {
                setError(err?.message ?? 'Failed to load items.');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadItems();
    }, [loadItems, refreshKey]);

    // Revalidate when the tab regains focus/visibility after being idle a while, so a
    // long-lived session (e.g. the app left open overnight) picks up server-side changes
    // like the nightly Plex sync without needing a manual reload. Throttled so normal
    // tab-switching doesn't trigger repeated full refetches.
    useEffect(() => {
        const REVALIDATE_AFTER_MS = 2 * 60 * 1000;
        const maybeRevalidate = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            if (!hasLoadedRef.current) return;
            if (Date.now() - lastFetchRef.current < REVALIDATE_AFTER_MS) return;
            loadItems();
        };
        document.addEventListener('visibilitychange', maybeRevalidate);
        window.addEventListener('focus', maybeRevalidate);
        return () => {
            document.removeEventListener('visibilitychange', maybeRevalidate);
            window.removeEventListener('focus', maybeRevalidate);
        };
    }, [loadItems]);

    // Called by the parent when the active view changes so the next load shows the skeleton
    const resetLoaded = useCallback(() => {
        hasLoadedRef.current = false;
    }, []);

    const updateItemField = useCallback((item, field, value) => {
        const id = getItemId?.(item) ?? item.id;
        setItems((prev) => prev.map((i) => {
            const iId = getItemId?.(i) ?? i.id;
            return iId === id ? { ...i, [field]: value } : i;
        }));
    }, [getItemId]);

    // Patches an item that is already loaded. Returns false when the item isn't in the
    // list (e.g. just added and not fetched yet) so the caller can refetch instead.
    const replaceItem = useCallback((id, updatedFields) => {
        const matches = (i) => (getItemId?.(i) ?? i.id) === id || i.id === id;
        const found = itemsRef.current.some(matches);
        if (found) {
            setItems((prev) => prev.map((i) => (matches(i) ? { ...i, ...updatedFields } : i)));
        }
        return found;
    }, [getItemId]);

    const removeItem = useCallback((id) => {
        setItems((prev) => prev.filter((i) => {
            const iId = getItemId?.(i) ?? i.id;
            return iId !== id && i.id !== id;
        }));
    }, [getItemId]);

    useEffect(() => {
        onItemActions?.({ replaceItem, removeItem });
    }, [onItemActions, replaceItem, removeItem]);

    return {
        items,
        loading,
        error,
        staleWarning,
        setStaleWarning,
        loadItems,
        resetLoaded,
        updateItemField,
        replaceItem,
        removeItem,
    };
}
